// src/analytics/AnalyticsProvider.tsx
import { useEffect, useState, useMemo, useCallback, useRef, type ReactNode } from 'react';
import { AnalyticsContext, type AnalyticsContextValue } from './AnalyticsContext';
import { initializeAmplitude } from './analyticsConfig';
import {
  track as trackEvent,
  setUserId,
  resetIdentity,
  extractSessionId,
} from './analyticsService';
import { useAuth } from '../auth/useAuth';
import type { TrackingEvent, EventAttributes } from './trackingTypes';

interface AnalyticsProviderProps {
  readonly children: ReactNode;
}

export function AnalyticsProvider({ children }: AnalyticsProviderProps): JSX.Element {
  const [isInitialized, setIsInitialized] = useState(false);
  const { user, isAuthenticated, isLoading } = useAuth();
  
  // Track whether we've fired the session restored event to prevent duplicates
  const hasTrackedSessionRestored = useRef(false);

  // Initialize Amplitude on mount
  useEffect(() => {
    initializeAmplitude();
    setIsInitialized(true);
  }, []);

  // Set/reset user identity based on auth state
  useEffect(() => {
    if (!isInitialized) return;

    if (isAuthenticated && user?.access_token) {
      const sessionId = extractSessionId(user.access_token);
      if (sessionId) {
        setUserId(sessionId);
      }
    } else {
      resetIdentity();
    }
  }, [isInitialized, isAuthenticated, user?.access_token]);

  // Track session restored event when app loads with existing session
  // This fires once when: analytics is initialized, auth loading is complete, and user is authenticated
  useEffect(() => {
    if (!isInitialized || isLoading || hasTrackedSessionRestored.current) return;

    if (isAuthenticated) {
      trackEvent('Pocket Admin Session Restored');
      hasTrackedSessionRestored.current = true;
    }
  }, [isInitialized, isLoading, isAuthenticated]);

  const track = useCallback(
    (event: TrackingEvent, attributes?: EventAttributes): void => {
      if (isInitialized) {
        trackEvent(event, attributes);
      }
    },
    [isInitialized]
  );

  const contextValue = useMemo<AnalyticsContextValue>(
    () => ({
      track,
      isInitialized,
    }),
    [track, isInitialized]
  );

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
}
